import React, { startTransition, useCallback, useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MapView from '../components/Map/MapView';
import MapLoadingOverlay from '../components/Map/MapLoadingOverlay';
import ResourcesPanel from '../components/ResourcesPanel/ResourcesPanel';
import AuthBar from '../components/Auth/AuthBar';
import SupportBanner from '../components/SupportBanner/SupportBanner';
import FiltersModal from '../components/FiltersModal/FiltersModal';
import LocationRatingBlock from '../components/LocationRatingBlock';
import { usePlacesAutocomplete } from '../hooks/usePlacesAutocomplete';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useSearchState } from '../contexts/SearchStateContext';
import { useSearchFlow } from '../hooks/useSearchFlow';
import { useZctaData } from '../hooks/useZctaData';
import { useZctaScores } from '../hooks/useZctaScores';
import { useFFListings } from '../hooks/useFFListings';
import { useAutocompleteKeyboard } from '../hooks/useAutocompleteKeyboard';
import { useRecentSearches } from '../hooks/useRecentSearches';
import { useSavedSearches } from '../hooks/useSavedSearches';
import { useAuth } from '../contexts/AuthContext';
import { reverseGeocode } from '../utils/geocodeWithMaps';
import { buildStateSuggestion } from '../utils/searchScopes';
import { getFeatureConfig, fetchFeatureConfig, CONFIG_UPDATED_EVENT } from '../utils/featureConfig';
import { isTenantCustomListingsFeatureEnabled } from '../utils/tenantListingsConfig';
import { mapsUrls } from '../components/Map/mapUtils';
import './Search.css';

const INITIAL_LISTINGS_PROGRESS = {
  phase: 'idle',
  rendered: 0,
  indexed: 0,
  total: 0,
  sourceTotal: 0,
  visibleTotal: 0,
  done: true,
  startedAt: 0,
  updatedAt: 0,
};

function Search() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const initialWorking = state.workingAddress || searchParams.get('q') || '';
  const initialView = (['map', 'list', 'resources'].includes(searchParams.get('view'))
    ? searchParams.get('view')
    : 'map');

  const [workingAddress, setWorkingAddress] = useState(initialWorking);
  const {
    favorites, favoriteStoreNames, classifyFavorite, petsFilter, setPetsFilter,
    ffMinPrice, setFfMinPrice, ffMaxPrice, setFfMaxPrice,
    ffMinBeds, setFfMinBeds, ffMinBaths, setFfMinBaths,
    showStoreMarkers, setShowStoreMarkers,
    showGymMarkers, setShowGymMarkers,
    showThingsToDo, setShowThingsToDo,
    showFFMarkers, setShowFFMarkers,
    showRentals, setShowRentals,
    mapLayer, setMapLayer,
    crimeLayerOpacity, setCrimeLayerOpacity,
    showCrimeLayer, setShowCrimeLayer,
    showTenantCustomListings, setShowTenantCustomListings,
    inactiveTenantListings, toggleTenantListingInactive,
    favoriteTenantListings, toggleTenantListingFavorite,
  } = useUserPreferences();

  const { user, loading: authLoading } = useAuth();

  const [featureConfig, setFeatureConfig] = useState(() => getFeatureConfig());
  const [mapReady, setMapReady] = useState(false);
  const [zctaReady, setZctaReady] = useState(false);
  const [thingsToDoReady, setThingsToDoReady] = useState(false);
  const [searchKey, setSearchKey] = useState(0);
  const [listingsRenderProgress, setListingsRenderProgress] = useState({
    phase: 'idle',
    rendered: 0,
    indexed: 0,
    total: 0,
    sourceTotal: 0,
    visibleTotal: 0,
    done: true,
    startedAt: 0,
    updatedAt: 0,
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [favoriteSectionOpen, setFavoriteSectionOpen] = useState(true);
  const [groceriesSectionOpen, setGroceriesSectionOpen] = useState(true);
  const [gymsSectionOpen, setGymsSectionOpen] = useState(true);
  const [openPlaceKey, setOpenPlaceKey] = useState(null);
  const [focusedMarker, setFocusedMarker] = useState(null);
  const [activeView, setActiveView] = useState(initialView);
  const { savedSearches, savedSearchesOpen, setSavedSearchesOpen, saveCurrentSearch } = useSavedSearches({ user, authLoading });
  const addressesWrapRef = useRef(null);
  const lastListingsProgressRef = useRef({
    phase: 'idle',
    rendered: 0,
    indexed: 0,
    total: 0,
    sourceTotal: 0,
    visibleTotal: 0,
    done: true,
    startedAt: 0,
    updatedAt: 0,
    ts: 0,
  });
  const { updateSearchState } = useSearchState();

  const handleListingsRenderProgress = useCallback((nextProgress) => {
    const now = Date.now();
    const prev = lastListingsProgressRef.current;
    const next = {
      phase: nextProgress?.phase || prev.phase || 'rendering',
      rendered: nextProgress?.rendered ?? 0,
      indexed: nextProgress?.indexed ?? prev.indexed ?? 0,
      total: nextProgress?.total ?? 0,
      sourceTotal: nextProgress?.sourceTotal ?? prev.sourceTotal ?? (nextProgress?.total ?? 0),
      visibleTotal: nextProgress?.visibleTotal ?? prev.visibleTotal ?? (nextProgress?.total ?? 0),
      done: !!nextProgress?.done,
      startedAt: Number(nextProgress?.startedAt) || prev.startedAt || now,
      updatedAt: Number(nextProgress?.updatedAt) || now,
    };

    const totalChanged = next.total !== prev.total;
    const phaseChanged = next.phase !== prev.phase;
    const indexingDelta = next.indexed - (prev.indexed ?? 0);
    const renderedDelta = next.rendered - prev.rendered;
    const isIdentical = !totalChanged
      && !phaseChanged
      && !next.done
      && renderedDelta === 0
      && indexingDelta === 0
      && next.visibleTotal === prev.visibleTotal;
    if (isIdentical) return;

    const enoughTimeElapsed = now - prev.ts >= 180;
    const shouldFlush = totalChanged || phaseChanged || next.done || renderedDelta >= 56 || indexingDelta >= 160 || enoughTimeElapsed;

    if (!shouldFlush) return;

    lastListingsProgressRef.current = { ...next, ts: now };
    startTransition(() => {
      setListingsRenderProgress(next);
    });
  }, []);

  const hideCrimeOverlay = !!featureConfig.hideCrimeOverlay;
  const hideSafetyScore = !!featureConfig.hideSafetyScore;
  const hideAffordabilityScore = !!featureConfig.hideAffordabilityScore;
  const hideLivabilityScore = !!featureConfig.hideLivabilityScore;
  const hideFurnishedFinder = !!featureConfig.hideFurnishedFinder;

  const tenantCustomListingsFeatureEnabled = isTenantCustomListingsFeatureEnabled();

  const safetyOverlayEnabled = !hideCrimeOverlay && !!showCrimeLayer;

  // "Applied" address — only updated on Search click or initial load
  const [workingAddressForFetch, setWorkingAddressForFetch] = useState(initialWorking.trim());

  // Sync activeView to ?view= param (replace so back button goes to Home, not previous tab)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentView = params.get('view') || 'map';
    if (currentView === activeView) return;
    const next = new URLSearchParams(params);
    if (activeView === 'map') {
      next.delete('view');
    } else {
      next.set('view', activeView);
    }
    navigate({ search: next.toString() }, { replace: true });
  }, [activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep document.title in sync with current search + view
  useEffect(() => {
    const viewLabel = activeView === 'list' ? 'Info' : activeView === 'resources' ? 'Resources' : 'Map';
    if (workingAddressForFetch) {
      document.title = `${workingAddressForFetch} — ${viewLabel} | Dwellia`;
    } else {
      document.title = 'Dwellia';
    }
    return () => { document.title = 'Dwellia'; };
  }, [workingAddressForFetch, activeView]);

  // --- Data hooks ---
  const { zctaCrimeData, zctaMedianRent, zctaLivabilityData } = useZctaData();

  const {
    status, error, workCenter, homeCenter, groceryStores, gyms, ffHtml, searchedState, searchScope,
  } = useSearchFlow({
    workingAddress: workingAddressForFetch,
    stayingAddress: '',
    favorites,
    favoriteStoreNames,
    hideFurnishedFinder,
    navigate,
    onClassifyFavorite: classifyFavorite,
  });

  const { safetyScores, affordabilityScores, livabilityScores, coverageAreaScores } = useZctaScores({
    workCenter, homeCenter, zctaCrimeData, zctaMedianRent, zctaLivabilityData, searchScope,
  });

  const { ffListings, ffPriceBounds, ffListingsFiltered } = useFFListings({
    ffHtml, petsFilter, ffMinPrice, ffMaxPrice, ffMinBeds, ffMinBaths,
  });

  // --- Effects ---
  useEffect(() => { updateSearchState({ workingAddress, stayingAddress: '' }); }, [workingAddress, updateSearchState]);
  useLayoutEffect(() => {
    document.body.classList.add('page-search');
    return () => {
      document.body.classList.remove('page-search');
      // Defensive teardown so map fullscreen / scroll locks never survive route change.
      document.body.style.overflow = '';
      if (typeof document !== 'undefined' && document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    fetchFeatureConfig().then((cfg) => setFeatureConfig(cfg));
    const syncConfig = () => setFeatureConfig(getFeatureConfig());
    const onConfigUpdated = (event) => {
      if (event?.detail && typeof event.detail === 'object') setFeatureConfig(event.detail);
      else syncConfig();
    };
    window.addEventListener(CONFIG_UPDATED_EVENT, onConfigUpdated);
    window.addEventListener('storage', syncConfig);
    return () => { window.removeEventListener(CONFIG_UPDATED_EVENT, onConfigUpdated); window.removeEventListener('storage', syncConfig); };
  }, []);

  // When FF is hidden, uncheck FF Listings
  useEffect(() => {
    if (hideFurnishedFinder) setShowFFMarkers(false);
  }, [hideFurnishedFinder, setShowFFMarkers]);

  // Clamp saved FF price filters into available range
  useEffect(() => {
    const { min, max } = ffPriceBounds;
    if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max)) return;
    const clampedMin = Math.max(min, Math.min(ffMinPrice ?? min, max));
    const clampedMax = Math.max(min, Math.min(ffMaxPrice ?? max, max));
    if (clampedMin !== ffMinPrice) setFfMinPrice(clampedMin);
    if (clampedMax !== ffMaxPrice) setFfMaxPrice(clampedMax);
  }, [ffPriceBounds, ffMinPrice, ffMaxPrice, setFfMinPrice, setFfMaxPrice]);

  // --- Autocomplete ---
  const workingAc = usePlacesAutocomplete(workingAddress, { prioritizeHealthcare: true });
  const { addRecentSearch, removeRecentSearch, getFilteredRecent } = useRecentSearches();

  const workingRecentMatches = useMemo(() => getFilteredRecent(workingAddress), [workingAddress, getFilteredRecent]);

  const workingCombined = useMemo(() => {
    const recentDescs = new Set(workingRecentMatches.map((r) => r.description.toLowerCase()));
    const fresh = workingAc.suggestions.filter((s) => !recentDescs.has(s.description.toLowerCase()));
    const stateSuggestion = buildStateSuggestion(workingAddress);
    const merged = [...workingRecentMatches, ...fresh];
    if (stateSuggestion && !merged.some((s) => (s.description || '').toLowerCase() === stateSuggestion.description.toLowerCase())) {
      merged.unshift(stateSuggestion);
    }

    const savedMatches = (savedSearches || []).filter((saved) => {
      const trimmed = (workingAddress || '').trim().toLowerCase();
      return !trimmed || saved.toLowerCase().includes(trimmed);
    });

    if (savedMatches.length > 0) {
      merged.unshift({
        place_id: 'saved-searches-group',
        description: 'Saved searches',
        structured_formatting: {
          main_text: 'Saved searches',
          secondary_text: `${savedMatches.length} saved`,
        },
        _savedSearchGroup: true,
        _savedSearches: savedMatches,
      });
    }

    return merged;
  }, [workingRecentMatches, workingAc.suggestions, workingAddress, savedSearches]);

  const suggestions = workingCombined;
  const showWorkingDropdown = activeDropdown === 'working' && (workingCombined.length > 0 || workingAc.loading);

  useEffect(() => { setHighlightedIndex(-1); }, [suggestions]);

  useEffect(() => {
    if (!groceriesSectionOpen && !gymsSectionOpen) {
      setOpenPlaceKey(null);
    }
  }, [groceriesSectionOpen, gymsSectionOpen]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (addressesWrapRef.current?.contains(e.target)) return;
      setActiveDropdown(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSearchWithValues = (nextWorking) => {
    const work = (nextWorking || '').trim();
    if (!work) { navigate('/', { replace: true }); return; }

    const nextParams = new URLSearchParams(location.search);
    nextParams.set('q', work);
    navigate({ search: nextParams.toString() }, { replace: false });

    setWorkingAddressForFetch(work);
    setMapReady(false);
    setZctaReady(false);
    setThingsToDoReady(false);
    setSearchKey(k => k + 1);
    const resetTs = Date.now();
    lastListingsProgressRef.current = {
      phase: 'indexing',
      rendered: 0,
      indexed: 0,
      total: 0,
      sourceTotal: 0,
      visibleTotal: 0,
      done: false,
      startedAt: resetTs,
      updatedAt: resetTs,
      ts: resetTs,
    };
    setListingsRenderProgress({
      phase: 'indexing',
      rendered: 0,
      indexed: 0,
      total: 0,
      sourceTotal: 0,
      visibleTotal: 0,
      done: false,
      startedAt: resetTs,
      updatedAt: resetTs,
    });
  };

  const handleSearch = () => runSearchWithValues(workingAddress);

  const handleSaveCurrentSearch = () => saveCurrentSearch(workingAddress);

  const handleApplySavedSearch = (value) => {
    if (value === '__OPEN_SAVED_SEARCHES_MODAL__') {
      setSavedSearchesOpen(true);
      return;
    }
    const next = (value || '').trim();
    if (!next) return;
    setWorkingAddress(next);
    setActiveDropdown(null);
    runSearchWithValues(next);
  };

  const handleMapReady = useCallback(() => {
    setMapReady(true);
  }, []);

  const handleZctaReady = useCallback(() => {
    setZctaReady(true);
  }, []);

  const handleZctaUseLocation = async (lat, lng) => {
    const addr = await reverseGeocode(lat, lng);
    if (!addr || !addr.trim()) return;
    const trimmed = addr.trim();
    setWorkingAddress(trimmed);
    runSearchWithValues(trimmed);
  };

  const handleSelectSuggestion = (field, prediction) => {
    const pred = typeof prediction === 'string' ? { description: prediction } : prediction;
    if (pred._savedSearchGroup) return;
    const picked = pred.description || '';
    const nextWorking = field === 'working' ? picked : workingAddress;
    setWorkingAddress(nextWorking);
    workingAc.resetSession();
    addRecentSearch(pred);
    setHighlightedIndex(-1);
    setActiveDropdown(null);
    runSearchWithValues(nextWorking);
  };

  const handleWorkingKeyDown = useAutocompleteKeyboard({
    suggestions: workingCombined,
    isOpen: showWorkingDropdown,
    onSelect: (s) => handleSelectSuggestion('working', s),
    onClose: () => setActiveDropdown(null),
    highlightedIndex,
    setHighlightedIndex,
    onEnterNoSelection: handleSearch,
  });

  const handleFocusPlaceOnMap = useCallback((place, markerKey) => {
    if (!place || place.lat == null || place.lng == null) return;
    setOpenPlaceKey(markerKey);
    setFocusedMarker({ markerKey, lat: place.lat, lng: place.lng, ts: Date.now() });
  }, []);

  // --- Derived data for render ---
  const favoritePlaces = useMemo(
    () => groceryStores.filter((s) => s.isFavorite).sort((a, b) => (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity)),
    [groceryStores]
  );
  const groceryOnly = useMemo(
    () => groceryStores.filter((s) => s.isGrocery === true).sort((a, b) => (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity)),
    [groceryStores]
  );
  const gymsSorted = useMemo(
    () => [...gyms].sort((a, b) => (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity)),
    [gyms]
  );
  const ffListingPrices = useMemo(
    () => ffListings.map((l) => l.amountNumeric).filter((n) => typeof n === 'number' && Number.isFinite(n) && n > 0),
    [ffListings]
  );
  const isStateSearch = searchScope?.mode === 'state';

  const hasScores = (workCenter || homeCenter) && (
    (!hideSafetyScore && (safetyScores.work != null || safetyScores.home != null)) ||
    (!hideAffordabilityScore && (affordabilityScores.work != null || affordabilityScores.home != null)) ||
    (!hideLivabilityScore && (livabilityScores.work != null || livabilityScores.home != null))
  );

  const trimmedCurrentSearch = (workingAddress || '').trim();
  const currentSearchIsSaved = !!trimmedCurrentSearch
    && savedSearches.some((s) => (s || '').trim().toLowerCase() === trimmedCurrentSearch.toLowerCase());

  if (!workingAddressForFetch) return null;

  return (
    <div className="search-page">
      <header className="search-page__header-bar">
        <Link to="/" className="search-page__logo-link" aria-label="Dwellia home">
          <img src="/dwellia-logo.png?v=3" alt="Dwellia" className="search-page__logo" />
        </Link>
        <div className="search-page__header-search" ref={addressesWrapRef} aria-label="Search addresses">
          <AddressField
            id="search-working"
            placeholder="Enter a work address, city, state, or ZIP code"
            value={workingAddress}
            onChange={(v) => { setWorkingAddress(v); setActiveDropdown('working'); }}
            onFocus={() => setActiveDropdown('working')}
            onKeyDown={handleWorkingKeyDown}
            onSubmit={handleSearch}
            ac={workingAc} suggestions={workingCombined} showDropdown={showWorkingDropdown}
            highlightedIndex={highlightedIndex} onHighlight={setHighlightedIndex}
            onSelect={(pred) => handleSelectSuggestion('working', pred)}
            onRemoveSuggestion={(pred) => {
              if (!pred?.isRecent) return;
              removeRecentSearch(pred);
              setHighlightedIndex(-1);
            }}
            onApplySavedSearch={handleApplySavedSearch}
            suggestionsId="search-suggestions-working"
          />
        </div>
        <AuthBar />
      </header>

      <header className="search-page__banner search-page__banner--mobile-only">
        <div className="search-page__banner-addresses" ref={addressesWrapRef} aria-label="Search addresses">
          <AddressField
            id="search-working-mobile"
            placeholder="Enter a work address, city, state, or ZIP code"
            value={workingAddress}
            onChange={(v) => { setWorkingAddress(v); setActiveDropdown('working'); }}
            onFocus={() => setActiveDropdown('working')}
            onKeyDown={handleWorkingKeyDown}
            onSubmit={handleSearch}
            ac={workingAc} suggestions={workingCombined} showDropdown={showWorkingDropdown}
            highlightedIndex={highlightedIndex} onHighlight={setHighlightedIndex}
            onSelect={(pred) => handleSelectSuggestion('working', pred)}
            onRemoveSuggestion={(pred) => {
              if (!pred?.isRecent) return;
              removeRecentSearch(pred);
              setHighlightedIndex(-1);
            }}
            onApplySavedSearch={handleApplySavedSearch}
            suggestionsId="search-suggestions-working-mobile"
          />
        </div>
      </header>

      <div className="search-page__view-bar">
        <nav className="search-page__view-nav" aria-label="View navigation">
          <button type="button" className={`search-page__view-btn${activeView === 'map' ? ' search-page__view-btn--active' : ''}`} onClick={() => setActiveView('map')}>Map</button>
          <button type="button" className={`search-page__view-btn${activeView === 'list' ? ' search-page__view-btn--active' : ''}`} onClick={() => setActiveView('list')}>Info</button>
          <button type="button" className={`search-page__view-btn${activeView === 'resources' ? ' search-page__view-btn--active' : ''}`} onClick={() => setActiveView('resources')}>Resources</button>
        </nav>
      </div>

            {savedSearchesOpen && (
        <div
          className="saved-searches-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="saved-searches-title"
          onClick={(e) => { if (e.target === e.currentTarget) setSavedSearchesOpen(false); }}
        >
          <div className="saved-searches-modal">
            <div className="saved-searches-modal__header">
              <h2 id="saved-searches-title" className="saved-searches-modal__title">Saved searches</h2>
              <button
                type="button"
                className="saved-searches-modal__close"
                onClick={() => setSavedSearchesOpen(false)}
                aria-label="Close saved searches"
              >
                �
              </button>
            </div>

            <div className="saved-searches-modal__body">
              <section className="saved-searches-modal__section" aria-labelledby="saved-searches-current-title">
                <h3 id="saved-searches-current-title" className="saved-searches-modal__section-title">Add current search</h3>
                {trimmedCurrentSearch ? (
                  <>
                    <p className="saved-searches-modal__current">
                      Current: <span className="saved-searches-modal__current-value">{trimmedCurrentSearch}</span>
                    </p>
                    <button
                      type="button"
                      className={`saved-searches-modal__btn ${currentSearchIsSaved ? 'saved-searches-modal__btn--saved' : 'saved-searches-modal__btn--primary'}`}
                      onClick={handleSaveCurrentSearch}
                      disabled={currentSearchIsSaved}
                      aria-disabled={currentSearchIsSaved}
                    >
                      {currentSearchIsSaved ? 'Saved' : 'Add'}
                    </button>
                  </>
                ) : (
                  <p className="saved-searches-modal__empty">Enter a work address to save it.</p>
                )}
              </section>

              <section className="saved-searches-modal__section" aria-labelledby="saved-searches-list-title">
                <h3 id="saved-searches-list-title" className="saved-searches-modal__section-title">Your saved searches</h3>
                {savedSearches.length === 0 ? (
                  <p className="saved-searches-modal__empty">No saved addresses yet.</p>
                ) : (
                  <ul className="saved-searches-modal__list">
                    {savedSearches.map((saved) => (
                      <li key={saved} className="saved-searches-modal__item">
                        <button
                          type="button"
                          className="saved-searches-modal__item-btn"
                          onClick={() => handleApplySavedSearch(saved)}
                        >
                          {saved}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <div className="saved-searches-modal__footer">
              <button type="button" className="saved-searches-modal__btn saved-searches-modal__btn--primary" onClick={() => setSavedSearchesOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}      <div className="search-page__body" data-active-view={activeView}>
        <div className="search-page__resources-wrap">
          <ResourcesPanel
            searchedState={searchedState}
            workingAddress={workingAddressForFetch || workingAddress}
          />
        </div>
        <div className="search-page__content">
          <header className="search-page__header"><h1 className="search-page__title">Search results</h1></header>
          <main className="search-page__main">
            {hasScores && (
              <div className="search-page__safety-gauges" aria-label="Location rating, safety, affordability, and livability for work and home">
                {workCenter && ((!hideSafetyScore && safetyScores.work != null) || (!hideAffordabilityScore && affordabilityScores.work != null) || (!hideLivabilityScore && livabilityScores.work != null)) && (
                  <div className="search-page__location-rating-block">
                    <LocationRatingBlock safetyScore={safetyScores.work} affordabilityScore={affordabilityScores.work} livabilityScore={livabilityScores.work} livabilityBreakdown={livabilityScores.workBreakdown} contextLabel="work" hideSafety={hideSafetyScore} hideAffordability={hideAffordabilityScore} hideLivability={hideLivabilityScore} />
                    <span className="search-page__location-rating-block-label">Work</span>
                  </div>
                )}
                {homeCenter && ((!hideSafetyScore && safetyScores.home != null) || (!hideAffordabilityScore && affordabilityScores.home != null) || (!hideLivabilityScore && livabilityScores.home != null)) && (
                  <div className="search-page__location-rating-block">
                    <LocationRatingBlock safetyScore={safetyScores.home} affordabilityScore={affordabilityScores.home} livabilityScore={livabilityScores.home} livabilityBreakdown={livabilityScores.homeBreakdown} contextLabel="home" hideSafety={hideSafetyScore} hideAffordability={hideAffordabilityScore} hideLivability={hideLivabilityScore} />
                    <span className="search-page__location-rating-block-label">Home</span>
                  </div>
                )}
              </div>
            )}

            {status === 'success' && favoritePlaces.length > 0 && (
              <section className="search-page__favorite-places search-page__category-section" aria-labelledby="favorite-places-heading">
                <button
                  type="button"
                  id="favorite-places-heading"
                  className="search-page__category-heading"
                  onClick={() => setFavoriteSectionOpen((o) => !o)}
                  aria-expanded={favoriteSectionOpen}
                >
                  <span className="search-page__category-heading-text">Favorite places</span>
                  <span className="search-page__category-heading-icon" aria-hidden="true">{favoriteSectionOpen ? '▼' : '▶'}</span>
                </button>
                {favoriteSectionOpen && (
                  <ul className="search-page__groceries-list">
                    {favoritePlaces.map((store) => {
                      const markerKey = `store-${store.id}`;
                      return (
                        <PlaceListItem
                          key={`favorite-${store.id}`}
                          place={store}
                          open={openPlaceKey === markerKey}
                          onToggle={() => handleFocusPlaceOnMap(store, markerKey)}
                          onClose={() => setOpenPlaceKey(null)}
                        />
                      );
                    })}
                  </ul>
                )}
              </section>
            )}

            <section className="search-page__category-section" aria-labelledby="groceries-heading">
              <button type="button" id="groceries-heading" className="search-page__category-heading" onClick={() => setGroceriesSectionOpen((o) => !o)} aria-expanded={groceriesSectionOpen}>
                <span className="search-page__category-heading-text">Grocery stores</span>
                <span className="search-page__category-heading-icon" aria-hidden="true">{groceriesSectionOpen ? '▼' : '▶'}</span>
              </button>
              {groceriesSectionOpen && (
                <>
                  {status === 'loading' && <p className="search-page__loading">Loading…</p>}
                  {status === 'error' && error && <p className="search-page__error">{error}</p>}
                  {status === 'success' && (
                    <ul className="search-page__groceries-list">
                      {groceryOnly.length === 0 ? (
                        <li className="search-page__groceries-empty">{isStateSearch ? 'Nearby grocery results are disabled for state-wide searches.' : (workCenter || homeCenter ? 'No grocery stores found nearby.' : 'Enter a work address, city, or state to see nearby grocery stores.')}</li>
                      ) : groceryOnly.map((store) => {
                        const markerKey = `store-${store.id}`;
                        return (
                          <PlaceListItem
                            key={`grocery-${store.id}`}
                            place={store}
                            open={openPlaceKey === markerKey}
                            onToggle={() => handleFocusPlaceOnMap(store, markerKey)}
                            onClose={() => setOpenPlaceKey(null)}
                          />
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </section>

            <section className="search-page__category-section" aria-labelledby="gyms-heading">
              <button type="button" id="gyms-heading" className="search-page__category-heading" onClick={() => setGymsSectionOpen((o) => !o)} aria-expanded={gymsSectionOpen}>
                <span className="search-page__category-heading-text">Gyms</span>
                <span className="search-page__category-heading-icon" aria-hidden="true">{gymsSectionOpen ? '▼' : '▶'}</span>
              </button>
              {gymsSectionOpen && (
                <>
                  {status === 'loading' && <p className="search-page__loading">Loading…</p>}
                  {status === 'success' && (
                    <ul className="search-page__groceries-list">
                      {gymsSorted.length === 0 ? (
                        <li className="search-page__groceries-empty">{isStateSearch ? 'Nearby gym results are disabled for state-wide searches.' : (workCenter || homeCenter ? 'No gyms found nearby.' : 'Enter a work address, city, or state to see nearby gyms.')}</li>
                      ) : gymsSorted.map((gym) => {
                        const markerKey = `gym-${gym.id}`;
                        return (
                          <PlaceListItem
                            key={`gym-${gym.id}`}
                            place={gym}
                            open={openPlaceKey === markerKey}
                            onToggle={() => handleFocusPlaceOnMap(gym, markerKey)}
                            onClose={() => setOpenPlaceKey(null)}
                          />
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </section>

          </main>
        </div>

        <aside className="search-page__map-wrap" aria-label="Map of search location and nearby grocery stores">
          {workCenter || homeCenter ? (
            <div className="search-page__map-inner">
              <MapView
                workCenter={workCenter} homeCenter={homeCenter}
                workAddress={workingAddressForFetch || workingAddress}
                homeAddress=""
                stores={groceryStores} gyms={gyms}
                showStoreMarkers={showStoreMarkers} showGymMarkers={showGymMarkers}
                showThingsToDo={!isStateSearch && showThingsToDo}
                showFFMarkers={!isStateSearch && !hideFurnishedFinder && showFFMarkers}
                onOpenFilters={() => setFiltersModalOpen(true)}
                focusedMarker={focusedMarker}
                showRentals={showRentals}
                onShowRentalsChange={setShowRentals}
                onShowFFMarkersChange={setShowFFMarkers}
                ffListings={ffListingsFiltered}
                zctaLayerOpacity={crimeLayerOpacity} mapLayer={mapLayer}
                onMapLayerChange={setMapLayer}
                searchKey={searchKey}
                onMapReady={handleMapReady} onZctaReady={handleZctaReady}
                onThingsToDoReady={setThingsToDoReady}
                onListingsRenderProgress={handleListingsRenderProgress}
                zctaCrimeData={zctaCrimeData} coverageAreaScores={coverageAreaScores}
                showCrimeControls={!hideCrimeOverlay}
                showCrimeLayer={safetyOverlayEnabled}
                onShowCrimeLayerChange={setShowCrimeLayer}
                onZctaLayerOpacityChange={setCrimeLayerOpacity}
                hideSafetyScore={hideSafetyScore} hideAffordabilityScore={hideAffordabilityScore} hideLivabilityScore={hideLivabilityScore}
                onZctaUseLocation={handleZctaUseLocation}
                tenantListingsFeatureEnabled={tenantCustomListingsFeatureEnabled}
                showTenantCustomListings={showTenantCustomListings}
                onShowTenantCustomListingsChange={setShowTenantCustomListings}
                inactiveTenantListings={inactiveTenantListings}
                onToggleTenantListingInactive={toggleTenantListingInactive}
                favoriteTenantListings={favoriteTenantListings}
                onToggleTenantListingFavorite={toggleTenantListingFavorite}
              />
              <MapLoadingOverlay
                mapReady={mapReady} groceryReady={status === 'success'} gymsReady={status === 'success'}
                safetyReady={safetyOverlayEnabled ? zctaReady : true} thingsToDoReady={thingsToDoReady}
                rentalsReady={!!listingsRenderProgress?.done}
                rentalsProgress={listingsRenderProgress}
                rentalsBlocking={false}
                variant="fullpage"
              />
            </div>
          ) : (
            <div className="search-page__map-loading">Loading map…</div>
          )}
        </aside>
      </div>

      <FiltersModal
        isOpen={filtersModalOpen} onClose={() => setFiltersModalOpen(false)}
        showRentals={showRentals} onShowRentalsChange={setShowRentals}
        showFFMarkers={showFFMarkers} onShowFFMarkersChange={setShowFFMarkers}
        showTenantListingsToggle={tenantCustomListingsFeatureEnabled}
        showTenantCustomListings={showTenantCustomListings}
        onShowTenantCustomListingsChange={setShowTenantCustomListings}
        petsFilter={petsFilter} onPetsFilterChange={setPetsFilter}
        ffMinPrice={ffMinPrice} onFfMinPriceChange={setFfMinPrice}
        ffMaxPrice={ffMaxPrice} onFfMaxPriceChange={setFfMaxPrice}
        ffPriceMinAvailable={ffPriceBounds.min} ffPriceMaxAvailable={ffPriceBounds.max}
        ffListingPrices={ffListingPrices}
        ffMinBeds={ffMinBeds} onFfMinBedsChange={setFfMinBeds}
        ffMinBaths={ffMinBaths} onFfMinBathsChange={setFfMinBaths}
        showStoreMarkers={showStoreMarkers} onShowStoreMarkersChange={setShowStoreMarkers}
        showGymMarkers={showGymMarkers} onShowGymMarkersChange={setShowGymMarkers}
        showThingsToDo={!isStateSearch && showThingsToDo} onShowThingsToDoChange={setShowThingsToDo}
        crimeLayerOpacity={crimeLayerOpacity} onCrimeLayerOpacityChange={setCrimeLayerOpacity}
        showFurnishedFinderControls={!hideFurnishedFinder} showCrimeControls={!hideCrimeOverlay}
      />
      <SupportBanner />

      <nav className="search-page__mobile-nav" aria-label="Navigation">
        <button
          type="button"
          className={`search-page__mobile-nav-btn${activeView === 'map' ? ' search-page__mobile-nav-btn--active' : ''}`}
          onClick={() => setActiveView('map')}
        >
          <svg className="search-page__mobile-nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
          <span className="search-page__mobile-nav-label">Map</span>
        </button>
        <button
          type="button"
          className={`search-page__mobile-nav-btn${activeView === 'list' ? ' search-page__mobile-nav-btn--active' : ''}`}
          onClick={() => setActiveView('list')}
        >
          <svg className="search-page__mobile-nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
          <span className="search-page__mobile-nav-label">Info</span>
        </button>
        <button
          type="button"
          className={`search-page__mobile-nav-btn${activeView === 'resources' ? ' search-page__mobile-nav-btn--active' : ''}`}
          onClick={() => setActiveView('resources')}
        >
          <svg className="search-page__mobile-nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          <span className="search-page__mobile-nav-label">Resources</span>
        </button>
      </nav>
    </div>
  );
}

function PlaceListItem({ place, open, onToggle, onClose }) {
  const isPlaceId = place.id && typeof place.id === 'string' && !place.id.startsWith('fav-');
  const { google: googleUrl, apple: appleUrl } = mapsUrls(
    place.lat,
    place.lng,
    isPlaceId ? place.id : null,
    place.name
  );
  const reviewCount = Number(place.userRatingCount ?? 0);
  const hasRating = place.rating != null;
  const hasReviews = reviewCount > 0;

  return (
    <li className={`search-page__groceries-item search-page__place-item${open ? ' search-page__place-item--open' : ''}`}>
      <button
        type="button"
        className="search-page__place-trigger"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span>{place.name}{place.distanceMi != null && ` (${place.distanceMi}mi)`}</span>
      </button>
      {open && (
        <div className="search-page__place-popup" role="region" aria-label={`${place.name} details`}>
          <div className="search-page__place-popup-top-row">
            {(hasRating || hasReviews) && (
              <p className="search-page__place-popup-rating">
                {hasRating && <span className="search-page__place-popup-stars">★ {Number(place.rating).toFixed(1)}</span>}
                {hasReviews && <span className="search-page__place-popup-reviews"> ({reviewCount.toLocaleString()} reviews)</span>}
              </p>
            )}
            <button type="button" className="search-page__place-popup-close" onClick={onClose} aria-label={`Close ${place.name} details`}>
              X
            </button>
          </div>
          <div className="search-page__place-popup-actions">
            <a href={googleUrl} target="_blank" rel="noopener noreferrer">Open in Google Maps</a>
            <a href={appleUrl} target="_blank" rel="noopener noreferrer">Open in Apple Maps</a>
          </div>
        </div>
      )}
    </li>
  );
}

/** Reusable address input with autocomplete dropdown. */
function MapLegendCompact() {
  const items = [
    { key: 'ff', icon: '$2,400', style: 'search-page__map-key-icon--ff', label: 'Furnished rental' },
    { key: 'work', icon: '●', style: 'search-page__map-key-icon--work', label: 'Work address' },
    { key: 'home', icon: '●', style: 'search-page__map-key-icon--home', label: 'Living address' },
    { key: 'grocery', icon: '🛒', style: 'search-page__map-key-icon--grocery', label: 'Grocery store' },
    { key: 'favorite', icon: '★', style: 'search-page__map-key-icon--favorite', label: 'Favorite place' },
    { key: 'gym', icon: '🏃', style: 'search-page__map-key-icon--gym', label: 'Gym' },
    { key: 'spring', icon: '♨', style: 'search-page__map-key-icon--spring', label: 'Hot spring' },
    { key: 'ski', icon: '⛷', style: 'search-page__map-key-icon--ski', label: 'Ski resort' },
  ];

  return (
    <section className="search-page__map-key" aria-label="Map marker key">
      <h3 className="search-page__map-key-title">Map key</h3>
      <ul className="search-page__map-key-list">
        {items.map((item) => (
          <li key={item.key} className="search-page__map-key-item">
            <span className={`search-page__map-key-icon ${item.style}`} aria-hidden="true">{item.icon}</span>
            <span className="search-page__map-key-label">{item.label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AddressField({
  id, placeholder, value,
  onChange, onFocus, onKeyDown, onSubmit,
  ac, suggestions, showDropdown,
  highlightedIndex, onHighlight,
  onSelect, onRemoveSuggestion, onApplySavedSearch, suggestionsId,
}) {
  return (
    <div className="search-page__banner-address-field search-page__banner-address-field--pill">
      <div className="search-page__banner-input-wrap search-page__banner-input-wrap--pill">
        <div className="search-page__banner-input-inner search-page__banner-input-inner--pill">
          <input
            id={id} type="text" role="combobox"
            className={`search-page__banner-input search-page__banner-input--pill ${ac.loading ? 'search-page__banner-input--loading' : ''}`}
            placeholder={placeholder} value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={(e) => { onFocus(); e.target.select(); }}
            onKeyDown={onKeyDown}
            autoComplete="off" aria-autocomplete="list" aria-expanded={showDropdown} aria-controls={suggestionsId}
          />
          {ac.loading && <span className="search-page__banner-spinner search-page__banner-spinner--pill" aria-hidden="true" />}
          <button type="button" className="search-page__banner-search-icon-btn search-page__banner-search-icon-btn--inline" onClick={onSubmit} aria-label="Search with current address">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </button>
          {showDropdown && (
            <ul id={suggestionsId} className="search-page__banner-suggestions search-page__banner-suggestions--pill" role="listbox">
              {ac.loading && suggestions.length === 0 ? (
                <li className="search-page__banner-suggestion search-page__banner-suggestion--loading">Searching…</li>
              ) : suggestions.map((pred, i) => (
                <li
                  key={pred.place_id}
                  className={`search-page__banner-suggestion ${i === highlightedIndex ? 'search-page__banner-suggestion--highlighted' : ''}`}
                  role="option" aria-selected={i === highlightedIndex}
                  onMouseEnter={() => onHighlight(i)}
                  onMouseDown={(e) => { if (!pred._savedSearchGroup) { e.preventDefault(); onSelect(pred); } }}
                >
                  {pred._savedSearchGroup ? (
                    <>
                      <button
                        type="button"
                        className="search-page__saved-searches-header-btn"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onApplySavedSearch?.('__OPEN_SAVED_SEARCHES_MODAL__');
                        }}
                      >
                        <span className="search-page__suggestion-row">
                          <span className="search-page__suggestion-row-main">
                            <span className="search-page__suggestion-main">Saved searches</span>
                          </span>
                        </span>
                      </button>
                      <div className="search-page__saved-suggestions-list">
                        {pred._savedSearches.map((saved) => (
                          <button
                            key={saved}
                            type="button"
                            className="search-page__saved-suggestion-btn"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onApplySavedSearch?.(saved);
                            }}
                          >
                            {saved}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="search-page__suggestion-row">
                        <span className="search-page__suggestion-row-main">
                          {pred.isRecent && <span className="search-page__suggestion-recent-icon" aria-hidden="true">↺</span>}
                          {pred.structured_formatting?.main_text
                            ? <span className="search-page__suggestion-main">{pred.structured_formatting.main_text}</span>
                            : <span className="search-page__suggestion-main">{pred.description}</span>}
                        </span>
                        {pred.isRecent && (
                          <button
                            type="button"
                            className="search-page__suggestion-remove"
                            aria-label={`Remove recent search ${pred.description}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onRemoveSuggestion?.(pred);
                            }}
                          >
                            ×
                          </button>
                        )}
                      </span>
                      {pred.structured_formatting?.secondary_text && <span className="search-page__suggestion-secondary">{pred.structured_formatting.secondary_text}</span>}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default Search;


